import { useEffect, useRef, useState, useCallback } from 'react';
import * as pdfjsLib from 'pdfjs-dist/legacy/build/pdf';
import { ChevronLeft, ChevronRight, Move, Type, Trash2, PenTool } from 'lucide-react';

pdfjsLib.GlobalWorkerOptions.workerSrc = 'https://unpkg.com/pdfjs-dist@4.4.168/legacy/build/pdf.worker.min.mjs';

const SIG_RATIO = 0.45; // hauteur = largeur * 0.45 (identique au backend)

let TXT_SEQ = 0;

// Aperçu de la page PDF + signature déplaçable + textes libres déplaçables.
// onChange renvoie { page, x, y, width_frac, texts:[{text,x,y,size,page}] } (coords normalisées, origine haut-gauche).
export default function PdfSignPlacer({ pdfUrl, signatureUrl, onChange }) {
  const canvasRef = useRef(null);
  const wrapRef = useRef(null);
  const pdfRef = useRef(null);
  const drag = useRef(null);
  const [numPages, setNumPages] = useState(1);
  const [pageIndex, setPageIndex] = useState(0);
  const [docLoaded, setDocLoaded] = useState(false);
  const [dims, setDims] = useState({ w: 0, h: 0 });
  const [scale, setScale] = useState(1);
  const [sig, setSig] = useState({ x: 0.5, y: 0.78, wf: 0.42, page: 0 });
  const [texts, setTexts] = useState([]);
  const [selId, setSelId] = useState(null);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState(false);

  // Chargement du document
  useEffect(() => {
    let cancelled = false;
    let task = null;
    setReady(false); setError(false); setDocLoaded(false);
    (async () => {
      try {
        task = pdfjsLib.getDocument({ url: pdfUrl });
        const doc = await task.promise;
        if (cancelled) return;
        pdfRef.current = doc;
        setNumPages(doc.numPages);
        setPageIndex(doc.numPages - 1);
        setSig(s => ({ ...s, page: doc.numPages - 1 }));
        setDocLoaded(true);
      } catch (e) {
        if (!cancelled) setError(true);
      }
    })();
    return () => { cancelled = true; try { task && task.destroy(); } catch (e) {} };
  }, [pdfUrl]);

  // Rendu de la page courante
  useEffect(() => {
    if (!docLoaded) return;
    let cancelled = false;
    (async () => {
      const doc = pdfRef.current;
      if (!doc) return;
      try {
        const page = await doc.getPage(pageIndex + 1);
        if (cancelled) return;
        const targetW = Math.min(wrapRef.current?.clientWidth || 460, 460);
        const base = page.getViewport({ scale: 1 });
        const sc = targetW / base.width;
        const viewport = page.getViewport({ scale: sc });
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        canvas.width = viewport.width;
        canvas.height = viewport.height;
        setDims({ w: viewport.width, h: viewport.height });
        setScale(sc);
        await page.render({ canvasContext: ctx, viewport }).promise;
        if (!cancelled) setReady(true);
      } catch (e) {
        if (!cancelled) setError(true);
      }
    })();
    return () => { cancelled = true; };
  }, [pageIndex, docLoaded]);

  // Remonte l'état
  useEffect(() => {
    if (!onChange) return;
    onChange({
      page: sig.page, x: sig.x, y: sig.y, width_frac: sig.wf,
      texts: texts.map(t => ({ text: t.text, x: t.x, y: t.y, page: t.page, size: Math.round(t.px / (scale || 1)) })),
    });
  }, [sig, texts, scale]); // eslint-disable-line

  const boxPx = {
    left: sig.x * dims.w, top: sig.y * dims.h,
    width: sig.wf * dims.w, height: sig.wf * dims.w * SIG_RATIO,
  };

  const startDrag = (kind, id) => (e) => {
    e.preventDefault(); e.stopPropagation();
    if (kind === 'text') setSelId(id);
    const rect = canvasRef.current.getBoundingClientRect();
    const item = kind === 'sig' ? sig : texts.find(t => t.id === id);
    drag.current = { kind, id, startX: e.clientX, startY: e.clientY, origX: item.x, origY: item.y, scaleW: rect.width, scaleH: rect.height };
    e.currentTarget.setPointerCapture(e.pointerId);
  };
  const onMove = useCallback((e) => {
    if (!drag.current) return;
    const d = drag.current;
    let nx = d.origX + (e.clientX - d.startX) / d.scaleW;
    let ny = d.origY + (e.clientY - d.startY) / d.scaleH;
    nx = Math.max(0, Math.min(nx, 0.98));
    ny = Math.max(0, Math.min(ny, 0.98));
    if (d.kind === 'sig') {
      const maxX = 1 - sig.wf, maxY = 1 - sig.wf * SIG_RATIO;
      setSig(s => ({ ...s, x: Math.min(nx, maxX), y: Math.min(ny, maxY) }));
    } else {
      setTexts(ts => ts.map(t => t.id === d.id ? { ...t, x: nx, y: ny } : t));
    }
  }, [sig.wf]);
  const endDrag = (e) => { e.stopPropagation(); drag.current = null; };

  const addText = () => {
    const id = `t${++TXT_SEQ}`;
    setTexts(ts => [...ts, { id, text: 'Texte', x: 0.15, y: 0.15, px: 16, page: pageIndex }]);
    setSelId(id);
  };
  const updSel = (patch) => setTexts(ts => ts.map(t => t.id === selId ? { ...t, ...patch } : t));
  const delSel = () => { setTexts(ts => ts.filter(t => t.id !== selId)); setSelId(null); };
  const sel = texts.find(t => t.id === selId);

  return (
    <div className="space-y-2" data-testid="pdf-sign-placer">
      <div className="flex items-center justify-between text-xs text-[#6B7280]">
        <span className="flex items-center gap-1"><Move className="h-3.5 w-3.5" /> Glissez signature et textes où vous voulez</span>
        {numPages > 1 && (
          <div className="flex items-center gap-2">
            <button type="button" onClick={() => setPageIndex(p => Math.max(0, p - 1))} disabled={pageIndex === 0} className="p-1 rounded border border-[#8B7355]/40 disabled:opacity-40" data-testid="pdf-prev-page"><ChevronLeft className="h-4 w-4" /></button>
            <span>Page {pageIndex + 1}/{numPages}</span>
            <button type="button" onClick={() => setPageIndex(p => Math.min(numPages - 1, p + 1))} disabled={pageIndex === numPages - 1} className="p-1 rounded border border-[#8B7355]/40 disabled:opacity-40" data-testid="pdf-next-page"><ChevronRight className="h-4 w-4" /></button>
          </div>
        )}
      </div>

      <div className="flex flex-wrap gap-2">
        <button type="button" onClick={addText} className="inline-flex items-center gap-1 border border-[#8B7355] text-[#8B7355] px-2.5 py-1 rounded text-xs hover:bg-[#8B7355]/10" data-testid="add-text-btn">
          <Type className="h-3.5 w-3.5" /> Ajouter un texte
        </button>
        {sig.page !== pageIndex && (
          <button type="button" onClick={() => setSig(s => ({ ...s, page: pageIndex }))} className="inline-flex items-center gap-1 border border-emerald-500 text-emerald-700 px-2.5 py-1 rounded text-xs hover:bg-emerald-50" data-testid="move-sig-here-btn">
            <PenTool className="h-3.5 w-3.5" /> Poser la signature sur cette page
          </button>
        )}
      </div>

      <div ref={wrapRef} className="relative mx-auto border border-[#E5E0D8] rounded-lg overflow-hidden bg-gray-100" style={{ width: dims.w || '100%' }}>
        {error && <div className="p-6 text-center text-sm text-red-500">Impossible d'afficher l'aperçu du PDF.</div>}
        <canvas ref={canvasRef} className="block w-full" />

        {ready && !error && dims.w > 0 && sig.page === pageIndex && (
          <div role="button" data-testid="signature-drag-box"
            onPointerDown={startDrag('sig')} onPointerMove={onMove} onPointerUp={endDrag}
            className="absolute touch-none cursor-move flex items-center justify-center rounded border-2 border-dashed border-emerald-500 bg-emerald-500/10"
            style={{ left: boxPx.left, top: boxPx.top, width: boxPx.width, height: boxPx.height }}>
            {signatureUrl ? <img src={signatureUrl} alt="signature" className="w-full h-full object-contain pointer-events-none" draggable={false} />
              : <span className="text-[10px] font-semibold text-emerald-700 pointer-events-none">Signature</span>}
          </div>
        )}

        {ready && !error && dims.w > 0 && texts.filter(t => t.page === pageIndex).map(t => (
          <div key={t.id} role="button" data-testid="text-drag-box"
            onPointerDown={startDrag('text', t.id)} onPointerMove={onMove} onPointerUp={endDrag}
            onClick={(e) => { e.stopPropagation(); setSelId(t.id); }}
            className={`absolute touch-none cursor-move whitespace-pre font-bold leading-tight ${selId === t.id ? 'ring-2 ring-amber-500' : ''}`}
            style={{ left: t.x * dims.w, top: t.y * dims.h, fontSize: t.px, color: '#111', background: 'rgba(255,235,150,0.35)', padding: '0 2px' }}>
            {t.text || 'Texte'}
          </div>
        ))}
      </div>

      {sig.page === pageIndex && (
        <div className="flex items-center gap-2">
          <span className="text-xs text-[#6B7280] whitespace-nowrap">Taille signature</span>
          <input type="range" min="0.15" max="0.7" step="0.01" value={sig.wf}
            onChange={(e) => { const wf = parseFloat(e.target.value); setSig(s => ({ ...s, wf, x: Math.min(s.x, 1 - wf), y: Math.min(s.y, 1 - wf * SIG_RATIO) })); }}
            className="flex-1 accent-emerald-600" data-testid="signature-size-slider" />
        </div>
      )}

      {sel && (
        <div className="p-2 rounded-lg border border-amber-300 bg-amber-50 space-y-2" data-testid="text-editor-panel">
          <div className="flex items-center gap-2">
            <Type className="h-4 w-4 text-amber-700" />
            <input value={sel.text} onChange={(e) => updSel({ text: e.target.value })} placeholder="Ex: Franchise 150€ — 17/06/2026"
              className="flex-1 border rounded px-2 py-1 text-sm" data-testid="text-content-input" />
            <button type="button" onClick={delSel} className="text-red-500 hover:text-red-700 p-1" data-testid="text-delete-btn"><Trash2 className="h-4 w-4" /></button>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-[#6B7280] whitespace-nowrap">Taille texte</span>
            <input type="range" min="10" max="40" step="1" value={sel.px} onChange={(e) => updSel({ px: parseInt(e.target.value, 10) })} className="flex-1 accent-amber-600" data-testid="text-size-slider" />
          </div>
        </div>
      )}
    </div>
  );
}
